import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { qk } from '@/api/queries';
import { Button, Input, Modal, ModalFooter, ModalHeader, Textarea, useModalTitleId, useToast } from '@/design-system';
import { num } from '@/api/types';
import { updatePlan, type Plan } from './api';
import s from './plans.module.css';

/** «Редактировать тариф» (additive, P1-F03): saving creates a new plan version on the server. */
export function PlanEditor({ plan, onClose }: { plan: Plan; onClose: () => void }) {
  const { t } = useTranslation('plans');
  const { t: tc } = useTranslation('common');
  const titleId = useModalTitleId();
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(plan.name);
  const [price, setPrice] = useState(String(num(plan.price)));
  const [features, setFeatures] = useState(plan.features.join('\n'));
  const save = useMutation({
    mutationFn: () =>
      updatePlan(plan.id, {
        name: name.trim(),
        price: Number(price.replace(/\s/g, '')) || 0,
        features: features.split('\n').map((f) => f.trim()).filter(Boolean),
      }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: qk.plans });
      toast(t('editor.saved', { name: p.name, version: p.version }));
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Modal open onClose={onClose} labelledBy={titleId}>
      <ModalHeader id={titleId} title={t('editor.title')} sub={t('editor.sub')} onClose={onClose} closeLabel={tc('actions.close')} />
      <form className={s.form} onSubmit={submit}>
        <Input label={t('editor.name')} value={name} required onChange={(e) => setName(e.target.value)} />
        <Input label={t('editor.price')} value={price} inputMode="numeric" required onChange={(e) => setPrice(e.target.value)} />
        <Textarea label={t('editor.features')} rows={5} value={features} onChange={(e) => setFeatures(e.target.value)} />
        <ModalFooter padTop>
          <Button size={46} variant="secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button size={46} type="submit" disabled={save.isPending}>
            {t('editor.save')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
